import {
  isAtomCommandInterrupted,
  squashAtomCommandFailure,
} from "@cleo/client-runtime/state/runtime";
import type { ScopedThreadRef, WorldRoomProjection, WorldSessionProjection } from "@cleo/contracts";
import { useNavigate } from "@tanstack/react-router";
import { CircleAlert, FilterX, Pin } from "lucide-react";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type DragEvent,
  type ReactElement,
  type ReactNode,
} from "react";

import { threadLastActivityAt } from "@cleo/client-runtime/state/thread-settled";
import { sessionsUnderWords } from "@cleo/shared/sessionTidy";
import {
  arrangeRoomRailGroups,
  filterRoomRailGroups,
  flatRoomRailSessions,
  pinnedRoomRailSessions,
  roomRailSessionEntries,
  roomRailSessionSiblings,
  ROOM_RAIL_SORTS,
  type RoomRailPreferences,
  type RoomRailSort,
} from "~/neucleos/roomRailArrangement";
import { noteWorldSessionsRetired } from "~/neucleos/retiredWorldSessions";
import { useRoomRailPreferences } from "~/neucleos/roomRailPreferences";
import {
  RoomRailArrangementControls,
  RoomRailDragArea,
  RoomRailReorderItem,
  RoomRailSortSelect,
  type RoomRailDragData,
} from "./RoomRailArrangementControls";
import { showContextMenuFallback } from "~/contextMenuFallback";
import { requestConfirmDialog } from "~/confirmDialog";
import { useCopyToClipboard } from "~/hooks/useCopyToClipboard";
import { useThreadActionMenu } from "~/hooks/useThreadActionMenu";
import { cn, randomUUID } from "~/lib/utils";
import {
  addressedLineage,
  flattenRailNodes,
  projectRoomRailGroups,
  type RoomRailParkedRoom,
  type RoomRailRoom,
  type RoomRailSessionNode,
} from "~/neucleos/projectRoomRailGroups";
import {
  addressedParkedRooms,
  archivedParkedRooms,
  archivedRoomEntries,
  retiredSessionCount,
  searchArchivedRooms,
  searchParkedRooms,
  type ArchivedRoomEntry,
} from "~/neucleos/roomRailHidden";
import { sessionFace } from "~/neucleos/sessionFace";
import { useSessionSiblingBirth } from "~/neucleos/useSessionSiblingBirth";
import { useSessionTidyRun } from "~/neucleos/useSessionTidyRun";
import { reportRailPins } from "~/neucleos/sessionTidyClient";
import { useSessionLivenessRollups } from "~/neucleos/useWorldLiveness";
import type { SessionLivenessNode } from "~/neucleos/worldLiveness";
import { changeWorldSessionRetirement } from "~/neucleos/worldSessionRetirementClient";
import { setWorldRoomArchived } from "~/neucleos/worldRoomArchiveClient";
import {
  openWorldSession,
  readWorldRoomEntry,
  rootSessionIdOf,
} from "~/neucleos/worldSessionClient";
import { useWorldSessionTimes } from "~/neucleos/useWorldSessionTimes";
import { workroomEntrySessionId } from "~/neucleos/workroomDoor";
import { projectSessionExecutionLists, useWorldViewIfSeated } from "~/neucleos/worldView";
import { WORLD_ROOMS_LOADING_COPY, isWorldRoomDisplayComplete } from "~/neucleos/worldRoomDisplay";
import { type RootSessionStatus, moreUrgentRootSessionStatus } from "~/rootSessionStrip";
import { SESSION_RAIL_ACCENT_READ } from "~/sessionAccent";
import { useThreadShells } from "~/state/entities";
import { threadEnvironment } from "~/state/threads";
import { useAtomCommand } from "~/state/use-atom-command";
import { ParkedRoomCard } from "./ParkedRoomCard";
import { RailGroup } from "./RailGroup";
import { RoomArchiveMenu } from "./RoomArchiveMenu";
import { ArchivedRoomRow, RailRevealToggle } from "./RoomRailReveal";
import { attachDroppedFilesToSession } from "./RoomRailFileDrop";
import {
  mergePersistedSessionOrders,
  readActiveReorderGate,
  readThreadActiveOrderKey,
  useActiveReorderCommand,
} from "./RoomRailActiveOrder";
import { useRoomRailCompact } from "./RoomRailDensity";
import {
  applyRoomRailDrop,
  dragCarriesFiles,
  normalizeRoomRailQuery,
  planActiveReorder,
  ROOM_RAIL_SCOPES,
  resolveRoomRailCompactMetrics,
  searchRoomRailGroups,
  type RoomRailDropEnd,
  type RoomRailScope,
} from "./RoomRailSection.logic";
import { useThreadChangeRequestStateStore } from "~/threadChangeRequestStateStore";
import { scopedThreadKey } from "@cleo/client-runtime/environment";
import { useRoomRailShortcuts } from "./RoomRailShortcuts";
import { LocalRoomRailRows } from "./RoomRailLocalOrgans";
import {
  renderRailChip,
  renderRailGroup,
  type RailGroupOrgans,
  type RailRowOrgans,
} from "./RailNavigationRows";
import {
  worldRailNavigation,
  worldRailRow,
  type RailSessionRow,
} from "~/neucleos/railNavigationModel";
import { RoomHeader } from "./RoomHeader";
import { buildRoomActionMenuItems } from "./threadActionMenu.logic";
import { setWorldRecordLabel } from "../neucleos/worldRecordLabelClient";
import { useRootSessionDefaults } from "../neucleos/rootSessionDefaults";
import { announceRootSessionModelNote } from "../neucleos/rootSessionModelSources";
import { recordWorldSessionBirthAttempt } from "../neucleos/worldSessionBirthAttemptsClient";
import { useSessionCloseCeremony } from "./WorldSessionStrip";
import { NewGeneralRoomButton } from "./NewGeneralRoomButton";
import { SessionTidyDialog, type SessionTidyScope } from "./SessionTidyDialog";
import {
  RoomCardHeader,
  roomCardActions,
  roomCardStyle,
  type RoomCardAction,
} from "./RoomCardsRail";
import {
  GENERAL_CARD_ID,
  isRoomCardId,
  projectRoomRailCards,
  roomCardIdOf,
  type RoomRailCard,
} from "~/neucleos/projectRoomRailCards";
import { deskSessionMenuEntries, useLaunchableWorkroomsOutcome } from "~/neucleos/workroomLaunch";
import { useWorkroomSessionBirth } from "~/neucleos/useWorkroomSessionBirth";
import { useWorldSessionsChangedRefresh } from "~/neucleos/useWorldSessionsChangedRefresh";
import {
  QUIET_DAY_MS,
  birthBoundFromAnchors,
  isQuietSession,
  splitQuietRoomRailGroups,
  type RoomRailQuietRule,
} from "~/neucleos/roomRailQuiet";

/** The flat list's Earlier row, kept beside the cards' own. */
const EARLIER_FLAT_KEY = "flat";
/** Sessions retired elsewhere that the rail reads one entry each for; more read the World again. */
const RETIRED_ENTRY_READS_MAX = 8;
import { toastManager } from "./ui/toast";
import { TooltipProvider } from "./ui/tooltip";
import { roomHoverFacts, sessionHoverFacts, stripRoomLabel } from "./railHoverCard.logic";
import { roomStatusDescription } from "./RoomStatusIndicator";

/**
 * The ROOM RAIL (register RM-CAN-10, opened 2026-09-03 at the principal's
 * annotated mockup and the sitting's rulings; the path of record: "one row
 * per open room, expanding to its sessions"): rooms over sessions over
 * sub-sessions, and the room's memory beside them.
 *
 * - ONE FOLD (projectRoomRailGroups) knows the World's shape; this component
 *   renders it and wires doors for either room kind. The main region shows
 *   a shared Pinned list from both kinds, followed by general rooms. Scoped
 *   room views keep their hierarchy and use the same pin preference.
 * - SHAPE GRAMMAR: the Room has a quiet status gutter; sessions retain rings.
 *   Expanded Rooms omit redundant pips; collapsed Rooms show the breakdown.
 *   Plural-session disclosure keeps its independent chevron.
 * - EVERY ROW IS A DOOR: chips and sub-session rows reveal ⋮ and retire on
 *   hover AND focus (FD-CAN-20 law 5 carried to nested rows); right-click is
 *   the fast door everywhere; the room header reveals ⋮ and NV2's ✕.
 * - THREADS LEAVE THE RAIL: a thread lives in the session-view. No entrance
 *   animation ("scroll only, no auto scroll animation"); the Retired group is
 *   one bounded AnimatedList frame (FD-CAN-38).
 * - The dock's two-region law still owns sizing, scrolling, the divider and
 *   both latches (DeskDock); this section stays pure content.
 *
 * Carried verbatim: the FD-CAN-13 ladder on the rail's one seat (the Neucleos
 * identity, sessionAccent.ts); the FD-CAN-21 beam while a session works; the
 * FD-CAN-20 one-menu law through useThreadActionMenu (now speaking "session",
 * RM-CAN-10 law 7); NV2's close ceremony; FD-CAN-20's inline rename through
 * updateMetadata.
 */
const NO_SESSION_NODES: ReadonlyArray<SessionLivenessNode> = [];
const RAIL_MENU_ACCENT = SESSION_RAIL_ACCENT_READ;
const RETIRED_LATCH_KEY = "cleo:room-rail:retired-open";
const ARCHIVED_LATCH_KEY = "cleo:room-rail:archived-open";

export interface RoomRailSectionProps {
  readonly wide: boolean;
  /** A hand act that needs the wide dock (Local's "+New Child" name prompt). */
  readonly onExpand?: (() => void) | undefined;
  /**
   * Revealing a room in the list ("Show in room"): a navigation, not a hand
   * act on the dock, so it may decline to widen it (Widen Cleo, 2026-10-07).
   * Falls back to `onExpand`.
   */
  readonly onReveal?: (() => void) | undefined;
  /** A chip mounts only its existing room; the main region defaults to general rooms. */
  readonly roomId?: string | undefined;
  readonly renderRoomHeader?:
    | ((props: ComponentProps<typeof RoomHeader>) => ReactElement)
    | undefined;
  /**
   * The dock's scroll frame (the principal, 18 Oct 2026: "remove the header
   * items from the scroll area"). The rail's header — Pinned, the Rooms row,
   * search — stands above it and only the rows scroll inside it, so the
   * frame's scroll edges and a revealed row land below the header. Without
   * one the rail is a single column.
   */
  readonly scrollFrame?: ((rows: ReactNode) => ReactNode) | undefined;
}

export function RoomRailSection({
  wide,
  onExpand,
  onReveal,
  roomId,
  renderRoomHeader,
  scrollFrame,
}: RoomRailSectionProps): ReactElement | null {
  // The outer shell is safe EVERYWHERE the dock mounts: it only peeks the
  // world view. The ceremony hooks (atom commands) live in the inner
  // component, mounted exclusively inside a world-ready view.
  const view = useWorldViewIfSeated();
  if (view === null) return null;
  // RM-CAN-14: the same rail in Local. Local's organs fill the one row renderer
  // from folders and threads; nothing of a World is read or worn there. A
  // scoped chip (`roomId`) is a workroom's, which Local does not have.
  if (view.state.kind === "local")
    return roomId === undefined ? (
      <TooltipProvider delay={600}>
        <LocalRoomRailRows wide={wide} onExpand={onExpand} scrollFrame={scrollFrame} />
      </TooltipProvider>
    ) : null;
  if (view.state.kind !== "world-ready") return null;
  return (
    // RM-CAN-10 hover card (2026-09-15): an intent delay (600 ms, so a scan
    // down the rail stays quiet), then the card; the provider's grace period
    // lets the pointer walk the rail without re-waiting.
    <TooltipProvider delay={600}>
      <RoomRailRows
        key={`${view.state.worldKey}:${roomId ?? "general"}`}
        view={view}
        wide={wide}
        onExpand={onReveal ?? onExpand}
        roomId={roomId}
        renderRoomHeader={renderRoomHeader}
        scrollFrame={scrollFrame}
      />
    </TooltipProvider>
  );
}

function RoomRailRows({
  view,
  wide,
  onExpand,
  roomId,
  renderRoomHeader,
  scrollFrame,
}: RoomRailSectionProps & {
  readonly view: NonNullable<ReturnType<typeof useWorldViewIfSeated>>;
}): ReactElement | null {
  const navigate = useNavigate();
  const { state } = view;
  // Only part of the World is in while its room pages arrive: show what is
  // here, never an empty answer, and say the rest is still loading.
  const roomsComplete = state.kind !== "world-ready" || isWorldRoomDisplayComplete(state.index);
  const worldKey = state.kind === "world-ready" ? state.worldKey : null;
  const liveness = useSessionLivenessRollups(
    worldKey,
    state.kind === "world-ready" ? state.index.sessions : NO_SESSION_NODES,
  );
  const { closeWarning, requestClose } = useSessionCloseCeremony({
    activeSessionId: state.kind === "world-ready" ? state.activeSessionId : null,
    index: state.kind === "world-ready" ? state.index : null,
    liveness,
    worldKey,
  });
  // FD-CAN-20: a chip binds to its standing execution shell; thread-backed
  // rows serve the FULL menu (speaking "session"), unbound rows the reduced one.
  const shells = useThreadShells();
  const executionListsBySession = useMemo(
    () => projectSessionExecutionLists(shells, worldKey ?? ""),
    [shells, worldKey],
  );
  const executionBySession = useMemo(() => {
    const bySession = new Map<string, (typeof shells)[number]>();
    for (const [sessionId, list] of executionListsBySession) {
      if (list[0] !== undefined) bySession.set(sessionId, list[0]);
    }
    return bySession;
  }, [executionListsBySession]);
  // UP13: settle/restore and stop by shortcut act on the ADDRESSED session's
  // bound thread; the hook refuses while the terminal owns the keys.
  useRoomRailShortcuts({
    activeSessionId: state.kind === "world-ready" ? state.activeSessionId : null,
    executionBySession,
  });
  // UP13: the persisted manual order's write door (gated per environment),
  // and the keys this desk just wrote, held until the World echoes them so a
  // moved row never snaps back while its write is in flight.
  const reorderActive = useActiveReorderCommand();
  const [pendingKeys, setPendingKeys] = useState<ReadonlyMap<string, string>>(new Map());
  useEffect(() => {
    setPendingKeys((previous) => {
      let next: Map<string, string> | null = null;
      for (const [sessionId, key] of previous) {
        const shell = executionBySession.get(sessionId);
        if (shell !== undefined && readThreadActiveOrderKey(shell) === key) {
          next ??= new Map(previous);
          next.delete(sessionId);
        }
      }
      return next ?? previous;
    });
  }, [executionBySession]);
  // UP13: a file drag over a row lights that row; a drop attaches the files
  // to the session's draft and opens it — nothing is sent.
  const [fileDropTarget, setFileDropTarget] = useState<string | null>(null);
  // UP13: compact rows (a per-viewer latch) and the search row whose scope
  // is the same filter the Arrange menu keeps.
  const [compact, setCompact] = useRoomRailCompact();
  const [query, setQuery] = useState("");
  // Can 80: the rooms whose Earlier row is open (per viewer, this window only).
  const [earlierOpen, setEarlierOpen] = useState<ReadonlySet<string>>(new Set());
  // Can 80, layer 2: Tidy up, for every room (null scope) or one card's rooms.
  const [tidy, setTidy] = useState<{
    readonly scope: SessionTidyScope | null;
    readonly label: string | null;
  } | null>(null);
  // The fold's clock: a session crosses into Earlier within a minute of turning
  // quiet while the Desk stays open.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(tick);
  }, []);
  const toggleEarlier = (id: string) =>
    setEarlierOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const changeRequests = useThreadChangeRequestStateStore((store) => store.byThreadKey);
  // Fold choices: a room folds its sessions (open by default); a plural
  // session folds its sub-sessions (the ADDRESSED one starts unfolded).
  const retirementRequests = useRef(new Set<string>());
  const archiveRequests = useRef(new Set<string>());
  const [archiveBusy, setArchiveBusy] = useState<ReadonlySet<string>>(new Set());
  const archivedRooms = useMemo(
    () => (state.kind !== "world-ready" ? [] : archivedRoomEntries(state.index)),
    [state],
  );
  const [retirementBusy, setRetirementBusy] = useState<ReadonlySet<string>>(new Set());
  const {
    preferences,
    scope: preferencesScope,
    save: savePreferences,
    commit: commitArrangement,
    togglePin: toggleSessionPin,
  } = useRoomRailPreferences(worldKey ?? "");
  const roomFolds = preferences.roomFolds;
  // The person's rail pins and the session open now stay through a tidy.
  const tidyKeep = useMemo(
    () =>
      state.kind === "world-ready" && state.activeSessionId !== null
        ? [...new Set([...preferences.pinnedSessionIds, state.activeSessionId])]
        : preferences.pinnedSessionIds,
    [preferences.pinnedSessionIds, state],
  );
  // Cleo's own tidy keeps these too (W-ada4bb6f): the Desk hears the pins
  // whenever they change. Signed out, they are not the person's saved pins.
  useEffect(() => {
    if (!worldKey || preferencesScope === null) return;
    void reportRailPins(worldKey, preferences.pinnedSessionIds);
  }, [worldKey, preferencesScope, preferences.pinnedSessionIds]);
  // Keep each filter's disclosure choices without overwriting the full-room view.
  const roomFoldKey = (id: string) =>
    preferences.filter === "all" ? id : `${preferences.filter}:${id}`;
  const saveRoomFolds = (next: Record<string, boolean>) =>
    savePreferences({ ...preferences, roomFolds: next });
  const [dragging, setDragging] = useState(false);
  const [frozenActivity, setFrozenActivity] = useState<ReadonlyMap<string, number> | null>(null);
  const activity = useMemo(
    () =>
      new Map(
        [...executionListsBySession].map(([sessionId, executions]) => [
          sessionId,
          Math.max(
            0,
            ...executions.map((shell) => {
              const at = threadLastActivityAt(shell);
              return at ? Date.parse(at) || 0 : 0;
            }),
          ),
        ]),
      ),
    [executionListsBySession],
  );
  // Can 80: when each session this Desk knows first began here, against its
  // place in the World's birth order — the bound for sessions it does not know.
  const birthBound = useMemo(() => {
    if (state.kind !== "world-ready") return undefined;
    const bornSeqOf = new Map(state.index.sessions.map((row) => [row.sessionId, row.bornSeq]));
    const anchors: Array<readonly [number, number]> = [];
    for (const [sessionId, executions] of executionListsBySession) {
      const seq = bornSeqOf.get(sessionId);
      const began = Math.min(
        ...executions.map((shell) => Date.parse(shell.createdAt) || Number.POSITIVE_INFINITY),
      );
      if (seq !== undefined && Number.isFinite(began)) anchors.push([seq, began]);
    }
    return birthBoundFromAnchors(anchors);
  }, [executionListsBySession, state]);
  // Can 80: the World's own time for each open session, from any device
  // (Pocket included), read only while quiet sessions fold.
  const openSessionIds = useMemo(
    () =>
      state.kind === "world-ready"
        ? state.index.sessions
            .filter((row) => row.lifecycle !== "retired")
            .map((row) => row.sessionId)
        : [],
    [state],
  );
  // A session the World says is retired (archived on another device, by a
  // tidy of Cleo's, or by the rail's own tidy as it goes) leaves the rail by
  // its one entry, without a reload; more than a few at once read the World
  // again instead.
  const dropRetired = (retired: ReadonlyArray<string>) => {
    if (typeof worldKey !== "string") return;
    noteWorldSessionsRetired(worldKey, retired);
    if (retired.length > RETIRED_ENTRY_READS_MAX) return view.refresh();
    for (const sessionId of retired)
      void readWorldRoomEntry({ worldKey, sessionId }).then((outcome) => {
        if (outcome.ok) view.acceptEntryReadback(outcome.entry);
      });
  };
  const worldActivity = useWorldSessionTimes(
    worldKey,
    openSessionIds,
    preferences.quietDays > 0,
    now,
    dropRetired,
  );
  // Can 80: sessions Cleo archives leave the rail once her turn finishes.
  const lastTurnKey = useMemo(
    () =>
      shells.reduce((latest, shell) => {
        const at = shell.worldKey === worldKey ? shell.latestTurn?.completedAt : null;
        return typeof at === "string" && at > latest ? at : latest;
      }, ""),
    [shells, worldKey],
  );
  useWorldSessionsChangedRefresh({ worldKey, lastTurnKey, refresh: view.refresh });
  // Can 80 (W-105e4e0a): the rail's own tidy runs on with its dialog closed;
  // each session archived leaves the rail as it goes, and the World is read
  // again once the tidy is done.
  const tidyRun = useSessionTidyRun({
    worldKey,
    onArchived: dropRetired,
    onDone: (outcome) => {
      if (outcome.ok) view.refresh();
    },
  });
  // A finished tidy is forgotten when the dialog opens, so it previews afresh;
  // a running one is shown where it stands.
  const openTidy = (next: {
    readonly scope: SessionTidyScope | null;
    readonly label: string | null;
  }) => {
    tidyRun.dismiss();
    setTidy(next);
  };
  const foldChoices = preferences.sessionFolds;
  const railRef = useRef<HTMLDivElement>(null);
  const [revealSessionId, setRevealSessionId] = useState<string | null>(null);
  useLayoutEffect(() => {
    if (revealSessionId === null || !wide || state.kind !== "world-ready") return;
    const session = state.index.sessions.find((entry) => entry.sessionId === revealSessionId);
    const room = state.index.rooms.find((entry) => entry.roomId === session?.roomId);
    const rail = railRef.current;
    if (!session || !room || !rail) return;
    // Run after the menu closes and the expanded room commits. Reveal never
    // activates a launcher: those buttons create a new workroom session.
    const frame = requestAnimationFrame(() => {
      const original = Array.from(
        rail.querySelectorAll<HTMLElement>("[data-room-rail-session]:not([data-room-rail-pin])"),
      ).find((element) => element.dataset.roomRailSession === session.sessionId);
      const worldView = rail.closest("[data-neucleos-world-view]");
      // Music's one door is the context dock's seat; every other room is a card here.
      const launcher =
        room.kind === "workroom" && room.workroomKey === "music"
          ? worldView?.querySelector<HTMLElement>("[data-music-dock-toggle]")
          : null;
      const roomHeader = Array.from(
        rail.querySelectorAll<HTMLElement>("[data-room-rail-room]"),
      ).find((element) => element.dataset.roomRailRoom === room.roomId);
      const target = original ?? launcher ?? roomHeader;
      target?.scrollIntoView?.({ block: "nearest", inline: "nearest", behavior: "instant" });
      target?.focus({ preventScroll: true });
      setRevealSessionId(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [revealSessionId, wide, state, preferences, foldChoices]);
  // Inline rename, keyed by session: navigating or Escape drops the draft.
  const [renaming, setRenaming] = useState<{
    readonly sessionId: string;
    readonly threadRef: ScopedThreadRef;
    readonly title: string;
  } | null>(null);
  // A room's own rename (2026-09-10): inline on its header, committed through
  // the World door; the naming agent's Regenerate asks the room's root session.
  const [renamingRoom, setRenamingRoom] = useState<{
    readonly roomId: string;
    readonly title: string;
  } | null>(null);
  const rootDefaults = useRootSessionDefaults(worldKey ?? "");
  const commitRoomRename = () => {
    if (renamingRoom === null || worldKey === null) return;
    const label = renamingRoom.title.trim();
    setRenamingRoom(null);
    if (label === "") {
      toastManager.add({ type: "warning", title: "Room name cannot be empty" });
      return;
    }
    // The World confirms the name by reading the room with one of its root sessions.
    const sessionId =
      state.kind === "world-ready" ? rootSessionIdOf(state.index, renamingRoom.roomId) : undefined;
    if (sessionId === undefined) {
      toastManager.add({ type: "warning", title: "No session to confirm this room's name yet" });
      return;
    }
    void setWorldRecordLabel({
      worldKey,
      recordId: renamingRoom.roomId,
      label,
      sessionId,
    }).then((outcome) => {
      if (outcome.ok) view.acceptEntryReadback(outcome.entry);
      else
        toastManager.add({
          type: "error",
          title: "Room was not renamed",
          description: outcome.reason,
        });
    });
  };
  const regenerateRoomTitle = (entry: RoomRailRoom) => {
    const root = entry.sessions[0]?.session;
    const shell = root ? executionBySession.get(root.sessionId) : undefined;
    if (!root || !shell) {
      toastManager.add({ type: "warning", title: "No session to name this room from yet" });
      return;
    }
    void updateThreadMetadata({
      environmentId: shell.environmentId,
      input: { threadId: shell.id, regenerateTitle: true },
    });
  };
  const updateThreadMetadata = useAtomCommand(threadEnvironment.updateMetadata, {
    reportFailure: false,
  });
  const { copyToClipboard: copyId } = useCopyToClipboard<{ id: string; noun: string }>({
    onCopy: ({ id, noun }) => {
      toastManager.add({ type: "success", title: `${noun} ID copied`, description: id });
    },
    onError: () => {
      toastManager.add({ type: "error", title: "Failed to copy ID" });
    },
  });
  const { openMenu } = useThreadActionMenu({
    threadRef: null,
    projectCwd: null,
    changeRequestState: null,
    onStartRename: () => {},
    accent: RAIL_MENU_ACCENT,
  });
  const siblingBirth = useSessionSiblingBirth();
  // The room cards (the principal, 16 Oct 2026: general sessions are "just
  // another room"): every entitled Workroom is a card beside General, from the
  // same registry fold the retired door chips read.
  const launchOutcome = useLaunchableWorkroomsOutcome(roomId === undefined ? worldKey : null);
  const workroomEntries = useMemo(
    () => deskSessionMenuEntries(launchOutcome.boards),
    [launchOutcome.boards],
  );
  const { birth: birthWorkroomSession } = useWorkroomSessionBirth();
  if (state.kind !== "world-ready" || worldKey === null) return null;
  const { index, activeSessionId } = state;
  const pinnedEntries =
    roomId === undefined ? pinnedRoomRailSessions(index, preferences.pinnedSessionIds) : [];
  const activePin = pinnedEntries.some(({ node }) => node.session.sessionId === activeSessionId);
  const allGroups = projectRoomRailGroups(index, activeSessionId);
  const cardWorkroomKeys = new Set(workroomEntries.map((entry) => entry.workroomKey));
  // The main rail holds General and every Workroom that has a card; a room
  // whose door is a context-dock seat (Music) keeps that door.
  const includesRoom = (entry: RoomRailRoom | RoomRailParkedRoom) =>
    roomId === undefined
      ? entry.room.kind === "general" ||
        (entry.room.workroomKey !== null && cardWorkroomKeys.has(entry.room.workroomKey))
      : entry.room.roomId === roomId;
  const statusOf = (sessionId: string): RootSessionStatus | null =>
    liveness.get(sessionId)?.own ?? null;
  // A general room is its conversations (what the cockpit rail calls a
  // session): once every one is retired (a tidy, or its own Archive), the
  // room leaves the main rail and its sessions wait under Show retired
  // sessions. A room just made, with no session yet, stays (17 Oct 2026: 141
  // emptied rooms kept General as long as before a tidy).
  const retiredRoomIds = new Set(allGroups.parked.map((entry) => entry.room.roomId));
  const emptiedGeneralRoom = (entry: RoomRailRoom) =>
    roomId === undefined &&
    entry.room.kind === "general" &&
    !entry.standing &&
    entry.sessions.length === 0 &&
    retiredRoomIds.has(entry.room.roomId);
  const scopedGroups = {
    ...allGroups,
    rooms: allGroups.rooms.filter((entry) => includesRoom(entry) && !emptiedGeneralRoom(entry)),
    parked: allGroups.parked.filter(includesRoom),
  };
  // A card offers Tidy up when the World has a room for it, whatever the view
  // shows: its sessions may all be folded under Earlier.
  const tidyCardIds = new Set([
    GENERAL_CARD_ID,
    ...scopedGroups.rooms.map((entry) => roomCardIdOf(entry.room)),
  ]);
  // UP13: the World's persisted keys lead each sibling run; the device's
  // manual order places the keyless rest (one arrangement on every desk).
  const activeOrderKeyOf = (sessionId: string): string | null => {
    const pending = pendingKeys.get(sessionId);
    if (pending !== undefined) return pending;
    const shell = executionBySession.get(sessionId);
    return shell === undefined ? null : readThreadActiveOrderKey(shell);
  };
  const orderedGroups = arrangeRoomRailGroups(
    scopedGroups,
    mergePersistedSessionOrders(preferences, scopedGroups, activeOrderKeyOf),
    frozenActivity ?? activity,
  );
  const groups = searchRoomRailGroups(
    filterRoomRailGroups(orderedGroups, preferences.filter, statusOf),
    query,
  );
  const flatView = roomId === undefined && preferences.view === "sessions";
  const needle = normalizeRoomRailQuery(query);
  // Can 80 (17 Oct 2026): sessions quiet for the chosen days fold under
  // Earlier in the main rail. A live search shows everything it finds.
  const quietRule: RoomRailQuietRule | null =
    roomId === undefined && needle.length === 0 && preferences.quietDays > 0
      ? {
          activity: frozenActivity ?? activity,
          cutoff: now - preferences.quietDays * QUIET_DAY_MS,
          pinned: new Set(preferences.pinnedSessionIds),
          live: (sessionId) => statusOf(sessionId) !== null,
          birthBound,
          worldActivity,
        }
      : null;
  const { recent: recentGroups, quiet: quietGroups } = splitQuietRoomRailGroups(groups, quietRule);
  const flatAll = flatView
    ? flatRoomRailSessions(orderedGroups, preferences, frozenActivity ?? activity, statusOf).filter(
        ({ node }) =>
          needle.length === 0 || node.session.label.toLocaleLowerCase().includes(needle),
      )
    : [];
  const flatQuietCount =
    quietRule === null ? 0 : flatAll.filter(({ node }) => isQuietSession(node, quietRule)).length;
  const flatSessions =
    quietRule === null || earlierOpen.has(EARLIER_FLAT_KEY)
      ? flatAll
      : flatAll.filter(({ node }) => !isQuietSession(node, quietRule));
  // Compact changes presentation only: folds never hide sessions or change
  // the saved expanded tree. The same arranged tree supplies both widths.
  const listedSessions = flatView
    ? flatSessions
    : !wide && roomId === undefined
      ? roomRailSessionEntries(recentGroups)
      : null;
  const cardChoices = {
    cardOrder: preferences.cardOrder,
    pinnedCardIds: preferences.pinnedCardIds,
    query,
    sessionOrder: "given" as const,
    hideEmpty: preferences.filter !== "all",
  };
  const showCards = roomId === undefined && !flatView && wide;
  const allCards = showCards ? projectRoomRailCards(groups, workroomEntries, cardChoices) : [];
  const recentCards =
    showCards && quietRule !== null
      ? projectRoomRailCards(recentGroups, workroomEntries, {
          ...cardChoices,
          hideEmpty: false,
        })
      : allCards;
  // A card's Earlier count: its sessions (hands included) folded away.
  const quietCountByCard = new Map<string, number>();
  for (const entry of quietGroups.rooms) {
    const id = roomCardIdOf(entry.room);
    quietCountByCard.set(
      id,
      (quietCountByCard.get(id) ?? 0) + flattenRailNodes(entry.sessions).length,
    );
  }
  const cards: ReadonlyArray<RoomRailCard> = allCards.map((card) =>
    earlierOpen.has(card.id) ? card : (recentCards.find((entry) => entry.id === card.id) ?? card),
  );
  const moveCard = (cardId: string, overId: string, after: boolean) => {
    const ids = cards.map((card) => card.id).filter((id) => id !== cardId);
    const at = ids.indexOf(overId);
    if (at < 0) return;
    ids.splice(after ? at + 1 : at, 0, cardId);
    commitArrangement({ ...preferences, cardOrder: ids });
  };
  const cardAction = (card: RoomRailCard, index: number, action: RoomCardAction) => {
    if (action === "pin" || action === "unpin") {
      const pinned = preferences.pinnedCardIds.filter((id) => id !== card.id);
      commitArrangement({
        ...preferences,
        pinnedCardIds: action === "pin" ? [...pinned, card.id] : pinned,
      });
    } else if (action === "move-up" || action === "move-down") {
      const neighbor = cards[index + (action === "move-up" ? -1 : 1)];
      if (neighbor) moveCard(card.id, neighbor.id, action === "move-down");
    } else if (action === "tidy") {
      openTidy({
        scope:
          card.workroomKey === null
            ? { kind: "general" }
            : { kind: "workroom", workroomKey: card.workroomKey },
        label: card.label,
      });
    }
  };
  const newWorkroomSession = (workroom: { workroomKey: string | null; label: string }) => {
    if (workroom.workroomKey === null) return;
    void birthWorkroomSession({
      workroomKey: workroom.workroomKey,
      title: workroom.label,
      intent: "new",
    }).then((outcome) => {
      if (outcome.ok || outcome.kind === "busy") return;
      toastManager.add({
        type: "error",
        title: `${workroom.label} session was not born`,
        description: outcome.reason,
      });
    });
  };
  // Where no room card carries a Workroom's own + (the tight dock, the
  // Sessions view), the dock's + offers each entitled Workroom (W-d416f930).
  const plusWorkrooms =
    roomId === undefined && (flatView || !wide)
      ? workroomEntries.map((entry) => ({ workroomKey: entry.workroomKey, title: entry.title }))
      : [];
  const onPlusWorkroom = (workroom: { workroomKey: string; title: string }) =>
    newWorkroomSession({ workroomKey: workroom.workroomKey, label: workroom.title });
  const setSessionSort = (parentId: string, sort: RoomRailSort | "inherit") => {
    const overrides = { ...preferences.sortOverrides };
    if (sort === "inherit") delete overrides[parentId];
    else overrides[parentId] = sort;
    commitArrangement({ ...preferences, sortOverrides: overrides });
  };
  // UP13: after a manual move of a session, the moved thread's key (or a
  // fresh spread over a keyless run) goes through the reorder door when this
  // environment takes the command; otherwise the device order alone holds.
  const persistActiveOrder = (sessionId: string, next: RoomRailPreferences) => {
    const moved = executionBySession.get(sessionId);
    if (moved === undefined || !readActiveReorderGate(moved.environmentId)) return;
    const parentId = index.sessions.find((entry) => entry.sessionId === sessionId)?.parentId;
    if (parentId === undefined) return;
    const orderedIds = next.sessionOrders[parentId] ?? [];
    const planned = planActiveReorder({
      orderedIds,
      keysById: new Map(orderedIds.map((id) => [id, activeOrderKeyOf(id)])),
      movedId: sessionId,
    }).filter(({ id }) => executionBySession.has(id));
    if (planned.length === 0) return;
    const writes = planned.map(({ id, orderKey }) => {
      const shell = executionBySession.get(id)!;
      return { environmentId: shell.environmentId, threadId: shell.id, orderKey };
    });
    setPendingKeys(
      (previous) => new Map([...previous, ...planned.map((w) => [w.id, w.orderKey] as const)]),
    );
    void reorderActive(writes).then((landed) => {
      if (landed) return;
      setPendingKeys((previous) => {
        const next = new Map(previous);
        for (const { id } of planned) next.delete(id);
        return next;
      });
    });
  };
  // One drop law for both the keyboard step and the pointer: within a
  // section it reorders; across the pinned/room seam it pins or unpins.
  const drop = (item: RoomRailDragData, over: RoomRailDropEnd, after: boolean) => {
    // A card moves among cards only; nothing else drops on a card.
    if (isRoomCardId(item.id) || isRoomCardId(over.id)) {
      if (item.kind === "room" && isRoomCardId(item.id) && isRoomCardId(over.id))
        moveCard(item.id, over.id, after);
      return;
    }
    const outcome = applyRoomRailDrop(preferences, orderedGroups, item, over, after);
    if (outcome === null) return;
    commitArrangement(outcome.next, outcome.announcement);
    if (item.kind === "session" && outcome.destination.kind === "reorder")
      persistActiveOrder(item.id, outcome.next);
  };
  const move = (item: RoomRailDragData, overId: string, after: boolean) =>
    drop(item, { kind: item.kind, id: overId, parentId: item.parentId }, after);
  const wrapSession = (row: RailSessionRow, children: ReactNode) => {
    const session = index.sessions.find((entry) => entry.sessionId === row.id);
    if (session === undefined) return children;
    const siblings = roomRailSessionSiblings(groups, session);
    const at = siblings.indexOf(session.sessionId);
    const item: RoomRailDragData = {
      kind: "session",
      id: session.sessionId,
      parentId: session.parentId,
      label: session.label,
    };
    return (
      <RoomRailReorderItem
        key={session.sessionId}
        item={item}
        enabled={wide && !flatView}
        canUp={at > 0}
        canDown={at >= 0 && at < siblings.length - 1}
        onStep={(direction) => {
          const target = siblings[at + direction];
          if (target) move(item, target, direction === 1);
        }}
        sortControls={
          row.children.length > 0 ? (
            <RoomRailSortSelect
              label="Sub-session order"
              value={preferences.sortOverrides[session.sessionId] ?? "inherit"}
              inherit
              onChange={(sort) => setSessionSort(session.sessionId, sort)}
            />
          ) : undefined
        }
      >
        {children}
      </RoomRailReorderItem>
    );
  };

  const commitRename = () => {
    if (renaming === null) return;
    const title = renaming.title.trim();
    setRenaming(null);
    if (title === "") {
      toastManager.add({ type: "warning", title: "Session title cannot be empty" });
      return;
    }
    const sessionId = renaming.sessionId;
    void updateThreadMetadata({
      environmentId: renaming.threadRef.environmentId,
      input: { threadId: renaming.threadRef.threadId, title },
    }).then(async (result) => {
      if (result._tag === "Failure" && !isAtomCommandInterrupted(result)) {
        const error = squashAtomCommandFailure(result);
        toastManager.add({
          type: "error",
          title: "Failed to rename session",
          description: error instanceof Error ? error.message : "An error occurred.",
        });
        return;
      }
      // The World session wears the same word (2026-09-10): the thread title
      // is the Desk's, the session label is the World's, one rename names both.
      const labelled = await setWorldRecordLabel({ worldKey, recordId: sessionId, label: title });
      if (!labelled.ok) {
        toastManager.add({
          type: "warning",
          title: "Renamed here, not in the World",
          description: labelled.reason,
        });
      } else view.acceptEntryReadback(labelled.entry);
    });
  };
  const address = (sessionId: string) => {
    if (sessionId === activeSessionId) return;
    void navigate({ to: "/session/$sessionId", params: { sessionId } } as never);
  };
  const dropFilesOnSession = (
    session: Pick<WorldSessionProjection, "sessionId" | "label">,
    files: ReadonlyArray<File>,
  ) => {
    const shell = executionBySession.get(session.sessionId);
    if (shell === undefined) {
      toastManager.add({
        type: "warning",
        title: "Open the session before dropping files",
        description: `${session.label} has no thread to hold attachments yet.`,
      });
      address(session.sessionId);
      return;
    }
    void attachDroppedFilesToSession({
      threadRef: { environmentId: shell.environmentId, threadId: shell.id },
      files,
    }).then(({ attached, rejected }) => {
      if (rejected !== null)
        toastManager.add({
          type: "error",
          title: "Some files were not attached",
          description: rejected,
        });
      if (attached > 0 || rejected === null) address(session.sessionId);
    });
  };
  const fileDropHandlers = (session: Pick<WorldSessionProjection, "sessionId" | "label">) => ({
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (!dragCarriesFiles(event.dataTransfer)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      if (fileDropTarget !== session.sessionId) setFileDropTarget(session.sessionId);
    },
    onDragLeave: (event: DragEvent<HTMLElement>) => {
      if (!dragCarriesFiles(event.dataTransfer)) return;
      const next = event.relatedTarget;
      if (next instanceof Node && event.currentTarget.contains(next)) return;
      setFileDropTarget((current) => (current === session.sessionId ? null : current));
    },
    onDrop: (event: DragEvent<HTMLElement>) => {
      if (!dragCarriesFiles(event.dataTransfer)) return;
      event.preventDefault();
      event.stopPropagation();
      setFileDropTarget(null);
      dropFilesOnSession(session, Array.from(event.dataTransfer.files));
    },
  });
  const showInRoom = (session: WorldSessionProjection) => {
    const room = index.rooms.find((entry) => entry.roomId === session.roomId);
    if (!room || room.archived || session.lifecycle === "retired") return;
    onExpand?.();
    // A Workroom without a card (Music: its one door is the context dock's
    // seat) opens the exact session, then reveals that seat.
    if (
      room.kind === "workroom" &&
      roomId === undefined &&
      (room.workroomKey === null || !cardWorkroomKeys.has(room.workroomKey))
    ) {
      if (session.sessionId !== activeSessionId) {
        void navigate({
          to: "/session/$sessionId",
          params: { sessionId: session.sessionId },
        } as never).then(() => setRevealSessionId(session.sessionId));
        return;
      }
      setRevealSessionId(session.sessionId);
      return;
    }
    // Every other room is a card (16 Oct 2026): open the session's lineage
    // and its card, then reveal its row.
    const expanded = { ...foldChoices };
    for (const id of addressedLineage(index, session.sessionId)) expanded[`all:${id}`] = true;
    savePreferences({
      ...preferences,
      view: "rooms",
      filter: "all",
      roomFolds: { ...preferences.roomFolds, [room.roomId]: true, [roomCardIdOf(room)]: true },
      sessionFolds: expanded,
    });
    setRevealSessionId(session.sessionId);
  };
  // Only the World coordinator can retire a session after preserving its output.
  // Engagement close remains a separate viewer action. Answers whether the
  // World confirmed it, with the reason in the person's words when not; a
  // session already being changed answers null.
  const requestRetirement = async (
    session: WorldSessionProjection,
    cancel = false,
  ): Promise<{ readonly ok: true } | { readonly ok: false; readonly reason: string } | null> => {
    if (retirementRequests.current.has(session.sessionId)) return null;
    if (cancel && session.retirement == null) return null;
    retirementRequests.current.add(session.sessionId);
    setRetirementBusy(new Set(retirementRequests.current));
    try {
      const outcome = await changeWorldSessionRetirement({
        worldKey,
        sessionId: session.sessionId,
        roomId: session.roomId,
        operationId: session.retirement?.operationId ?? randomUUID(),
        ...(cancel ? { fence: session.retirement!.fence } : {}),
      });
      if (outcome.ok) {
        if ("entry" in outcome) view.acceptEntryReadback(outcome.entry);
        else view.acceptReadback(outcome.index);
        // W-ea4c5090: the agent strip and the Inbox's Finished cards drop it too.
        const lifecycle =
          "entry" in outcome
            ? outcome.entry.session.lifecycle
            : outcome.index.sessions.find((row) => row.sessionId === session.sessionId)?.lifecycle;
        if (lifecycle !== undefined)
          noteWorldSessionsRetired(worldKey, [session.sessionId], lifecycle === "retired");
        return { ok: true };
      }
      view.refresh();
      return { ok: false, reason: outcome.reason };
    } catch {
      view.refresh();
      return {
        ok: false,
        reason: "Refresh to check the session, then retry or cancel any pending retirement.",
      };
    } finally {
      retirementRequests.current.delete(session.sessionId);
      setRetirementBusy(new Set(retirementRequests.current));
    }
  };
  const changeRetirement = async (session: WorldSessionProjection, cancel = false) => {
    const outcome = await requestRetirement(session, cancel);
    if (outcome === null || outcome.ok) return;
    toastManager.add({
      type: "error",
      title: cancel ? "Retirement cancellation was not confirmed" : "Retirement was not confirmed",
      description: outcome.reason,
    });
  };
  // W-9b736ece (Jason's Designer room, 18 Oct 2026): the World never retires
  // a session while a session under it is open, so the open sessions under it
  // (its hands, and theirs) go first, the deepest first, each through the same
  // door; one that stays keeps every session above it, and the person reads
  // which and why.
  const openHandsOf = (session: WorldSessionProjection): ReadonlyArray<WorldSessionProjection> => {
    const under = new Map<string, WorldSessionProjection[]>();
    for (const row of index.sessions) {
      if (row.lifecycle === "retired" || row.parentId === row.sessionId) continue;
      const list = under.get(row.parentId);
      if (list === undefined) under.set(row.parentId, [row]);
      else list.push(row);
    }
    const hands: WorldSessionProjection[] = [];
    const seen = new Set([session.sessionId]);
    const visit = (sessionId: string) => {
      for (const hand of under.get(sessionId) ?? []) {
        if (seen.has(hand.sessionId)) continue;
        seen.add(hand.sessionId);
        visit(hand.sessionId);
        hands.push(hand);
      }
    };
    visit(session.sessionId);
    return hands;
  };
  const retireWithHands = async (
    session: WorldSessionProjection,
    hands: ReadonlyArray<WorldSessionProjection>,
  ) => {
    for (const hand of hands) {
      const outcome = await requestRetirement(hand);
      if (outcome === null || outcome.ok) continue;
      toastManager.add({
        type: "error",
        title: `${session.label} was not retired`,
        description: `“${hand.label}”, a session under it, could not be retired, so it stays and so does ${session.label}. ${outcome.reason}`,
      });
      return;
    }
    await changeRetirement(session);
  };
  // A 14px hover-revealed glyph must not retire a session on one slip
  // (2026-09-15 review): the themed confirm stands in front of the door,
  // from the glyph and from the menu alike. Without a themed host (a
  // headless story) the door answers directly.
  const retireSession = (session: WorldSessionProjection) => {
    const hands = openHandsOf(session);
    const themed = requestConfirmDialog(
      hands.length === 0
        ? `Retire ${session.label}? Its transcript joins the room's memory; continue it later from Retired (expand the dock and turn on Show retired sessions at the foot of the rail).`
        : `Retire ${session.label}? ${sessionsUnderWords(
            session.label,
            hands.map((hand) => hand.label),
            "retired",
          )} Their transcripts join the room's memory; continue them later from Retired (expand the dock and turn on Show retired sessions at the foot of the rail).`,
      { variant: "destructive" },
    );
    if (themed === undefined) {
      void retireWithHands(session, hands);
      return;
    }
    void themed.then((confirmed) => {
      if (confirmed) void retireWithHands(session, hands);
    });
  };
  const retirementControls = (session: WorldSessionProjection) =>
    session.retirement == null ? null : (
      <div
        className="flex flex-wrap items-center gap-2 px-2 text-[10px] text-muted-foreground"
        data-room-rail-retirement-pending={session.sessionId}
      >
        <span>Retirement pending</span>
        <button
          type="button"
          className="underline disabled:opacity-50"
          disabled={retirementBusy.has(session.sessionId)}
          data-room-rail-retirement-retry={session.sessionId}
          onClick={() => retireSession(session)}
        >
          Retry
        </button>
        <button
          type="button"
          className="underline disabled:opacity-50"
          disabled={retirementBusy.has(session.sessionId)}
          data-room-rail-retirement-cancel={session.sessionId}
          onClick={() => {
            void changeRetirement(session, true);
          }}
        >
          Cancel
        </button>
      </div>
    );
  const openSessionMenu = (
    session: WorldSessionProjection,
    position: { x: number; y: number },
    pinShortcut = false,
  ) => {
    const pinItem = {
      id: "session-pin",
      label: preferences.pinnedSessionIds.includes(session.sessionId)
        ? "Unpin session"
        : "Pin session",
    };
    const revealItems = pinShortcut
      ? [{ id: "show-in-room", label: "Show in room", icon: "folder-tree" }]
      : [];
    if (session.retirement != null) {
      void showContextMenuFallback(
        [
          pinItem,
          ...revealItems,
          { id: "retry-retirement", label: "Retry retirement" },
          { id: "cancel-retirement", label: "Cancel retirement" },
          { id: "copy-session-id", label: "Copy session ID", icon: "copy" },
        ],
        position,
        { accent: RAIL_MENU_ACCENT },
      ).then((action) => {
        if (action === "session-pin") toggleSessionPin(session.sessionId);
        else if (action === "show-in-room") showInRoom(session);
        else if (action === "retry-retirement") retireSession(session);
        else if (action === "cancel-retirement") void changeRetirement(session, true);
        else if (action === "copy-session-id")
          copyId(session.sessionId, { id: session.sessionId, noun: "Session" });
      });
      return;
    }
    const shell = executionBySession.get(session.sessionId);
    if (shell === undefined) {
      // The reduced menu — never dead thread items on an unbound row.
      void showContextMenuFallback(
        [
          pinItem,
          ...revealItems,
          { id: "close", label: "Close session" },
          { id: "retire", label: "Retire session" },
          { id: "copy-session-id", label: "Copy session ID", icon: "copy" },
        ],
        position,
        { accent: RAIL_MENU_ACCENT },
      ).then((action) => {
        if (action === "session-pin") toggleSessionPin(session.sessionId);
        else if (action === "show-in-room") showInRoom(session);
        else if (action === "close") requestClose(session.sessionId, session.label);
        else if (action === "retire") retireSession(session);
        else if (action === "copy-session-id")
          copyId(session.sessionId, { id: session.sessionId, noun: "Session" });
      });
      return;
    }
    const threadRef: ScopedThreadRef = { environmentId: shell.environmentId, threadId: shell.id };
    openMenu(position, {
      threadRef,
      noun: "session",
      ...(pinShortcut ? { onShowInRoom: () => showInRoom(session) } : {}),
      sessionPin: {
        pinned: preferences.pinnedSessionIds.includes(session.sessionId),
        onToggle: () => toggleSessionPin(session.sessionId),
      },
      onStartRename: () =>
        setRenaming({
          sessionId: session.sessionId,
          threadRef,
          title: shell.title ?? session.label,
        }),
      onSiblingBirth: ({ lineage, thread }) => {
        void siblingBirth({
          worldKey,
          sessionId: session.sessionId,
          sessionLabel: session.label,
          source: thread,
          lineage,
        });
      },
      onRetire: () => retireSession(session),
    });
  };
  // A new ROOT session in a room is a fresh Cleo seat on the root-session
  // defaults (2026-09-10): the same law as a new room's first session.
  const newSessionInRoom = (entry: RoomRailRoom) => {
    const sessionId = `S_${randomUUID().replaceAll("-", "")}`;
    void (async () => {
      const prepared = await rootDefaults.prepareExecution({ sessionId, label: "New session" });
      if (!prepared.ok) {
        toastManager.add({
          type: "error",
          title: "The session was not born",
          description: prepared.reason,
        });
        return;
      }
      if (prepared.execution) {
        const recorded = await recordWorldSessionBirthAttempt(prepared.execution);
        if (!recorded.ok) {
          toastManager.add({
            type: "error",
            title: "The session was not born",
            description: "The default model could not be reserved.",
          });
          return;
        }
      }
      const outcome = await openWorldSession({
        worldKey,
        sessionId,
        label: "New session",
        parentId: entry.room.roomId,
      });
      if (!outcome.ok) {
        toastManager.add({
          type: "error",
          title: "The session was not born",
          description: outcome.reason,
        });
        return;
      }
      view.acceptEntryReadback(outcome.entry);
      address(sessionId);
      if (prepared.note !== undefined) announceRootSessionModelNote(prepared.note);
    })();
  };
  const changeArchive = async (
    room: WorldRoomProjection,
    archived: boolean,
  ): Promise<string | null> => {
    if (archiveRequests.current.has(room.roomId)) return "This room change is already in progress.";
    // The World confirms the change by reading the room with one of its root sessions.
    const sessionId =
      state.kind === "world-ready" ? rootSessionIdOf(state.index, room.roomId) : undefined;
    if (sessionId === undefined) return "This room has no session to confirm the change with.";
    archiveRequests.current.add(room.roomId);
    setArchiveBusy(new Set(archiveRequests.current));
    try {
      const outcome = await setWorldRoomArchived({
        worldKey,
        roomId: room.roomId,
        archived,
        expectedArchiveSeq: room.archiveSeq ?? 0,
        sessionId,
      });
      if (outcome.ok) {
        if (!archived) {
          savePreferences({
            ...preferences,
            roomFolds: { ...preferences.roomFolds, [room.roomId]: true },
          });
        }
        view.acceptEntryReadback(outcome.entry);
        return null;
      }
      if (outcome.kind === "context_changed" || outcome.kind === "transport") view.refresh();
      toastManager.add({
        type: "error",
        title: archived ? "Room was not archived" : "Room was not restored",
        description: outcome.reason,
      });
      return outcome.reason;
    } catch {
      view.refresh();
      const reason = "The room change could not be confirmed. Refresh and retry.";
      toastManager.add({
        type: "error",
        title: archived ? "Room was not archived" : "Room was not restored",
        description: reason,
      });
      return reason;
    } finally {
      archiveRequests.current.delete(room.roomId);
      setArchiveBusy(new Set(archiveRequests.current));
    }
  };
  const archiveRoom = (entry: RoomRailRoom) => {
    void changeArchive(entry.room, true);
  };
  const openRoomMenu = (entry: RoomRailRoom, position: { x: number; y: number }) => {
    void showContextMenuFallback(
      [
        ...buildRoomActionMenuItems({
          openSessions: flattenRailNodes(entry.sessions).length,
          busy: archiveBusy.has(entry.room.roomId),
          canRegenerate: entry.sessions.length > 0,
        }),
        {
          id: "room-session-order",
          label: "Session order",
          children: [
            { id: "room-sort-inherit", label: "Use default" },
            ...Object.entries(ROOM_RAIL_SORTS).map(([sort, label]) => ({
              id: `room-sort-${sort}`,
              label: `${preferences.sortOverrides[entry.room.roomId] === sort ? "✓ " : ""}${label}`,
            })),
          ],
        },
        {
          id: "room-move-up",
          label: "Move up",
          disabled: groups.rooms[0]?.room.roomId === entry.room.roomId,
        },
        {
          id: "room-move-down",
          label: "Move down",
          disabled: groups.rooms.at(-1)?.room.roomId === entry.room.roomId,
        },
      ],
      position,
      { accent: RAIL_MENU_ACCENT },
    ).then((action) => {
      if (action === "room-rename")
        setRenamingRoom({ roomId: entry.room.roomId, title: entry.room.label });
      else if (action === "room-regenerate-title") regenerateRoomTitle(entry);
      else if (action?.startsWith("room-sort-"))
        setSessionSort(
          entry.room.roomId,
          action.slice("room-sort-".length) as RoomRailSort | "inherit",
        );
      else if (action === "room-move-up" || action === "room-move-down") {
        const direction = action === "room-move-up" ? -1 : 1;
        const target =
          groups.rooms[
            groups.rooms.findIndex((candidate) => candidate.room.roomId === entry.room.roomId) +
              direction
          ];
        if (target)
          move(
            { kind: "room", id: entry.room.roomId, parentId: worldKey, label: entry.room.label },
            target.room.roomId,
            direction === 1,
          );
      } else if (action === "room-new-session") newSessionInRoom(entry);
      else if (action === "room-copy-id")
        copyId(entry.room.roomId, { id: entry.room.roomId, noun: "Room" });
      else if (action === "room-archive") archiveRoom(entry);
    });
  };
  const openParkedRoomMenu = (entry: RoomRailParkedRoom, position: { x: number; y: number }) => {
    void showContextMenuFallback(
      [
        {
          id: "browse-history",
          label: "Browse history",
        },
        { id: "copy-room-id", label: "Copy room ID", icon: "copy" },
      ],
      position,
      { accent: RAIL_MENU_ACCENT },
    ).then((action) => {
      if (action === "browse-history" && entry.sessions[0]) address(entry.sessions[0].sessionId);
      else if (action === "copy-room-id")
        copyId(entry.room.roomId, { id: entry.room.roomId, noun: "Room" });
    });
  };
  const openParkedSessionMenu = (
    session: WorldSessionProjection,
    position: { x: number; y: number },
  ) => {
    void showContextMenuFallback(
      [
        { id: "browse-history", label: "Browse history" },
        { id: "copy-session-id", label: "Copy session ID", icon: "copy" },
      ],
      position,
      { accent: RAIL_MENU_ACCENT },
    ).then((action) => {
      if (action === "browse-history") address(session.sessionId);
      else if (action === "copy-session-id")
        copyId(session.sessionId, { id: session.sessionId, noun: "Session" });
    });
  };

  /** UP13 compact row: status word, PR badge and relative time under the label. */
  const compactMetrics = (sessionId: string) => {
    const shell = executionBySession.get(sessionId);
    return resolveRoomRailCompactMetrics({
      status: statusOf(sessionId),
      changeRequest:
        shell === undefined
          ? null
          : (changeRequests.get(
              scopedThreadKey({ environmentId: shell.environmentId, threadId: shell.id }),
            ) ?? null),
      lastActivityMs: (frozenActivity ?? activity).get(sessionId) ?? null,
      nowMs: Date.now(),
    });
  };
  const threadRefOf = (sessionId: string): ScopedThreadRef | null => {
    const shell = executionBySession.get(sessionId);
    return shell === undefined ? null : { environmentId: shell.environmentId, threadId: shell.id };
  };
  // RM-CAN-14: the World's organs for the one row renderer
  // (RailNavigationRows). The rail draws the navigation model's rows; every
  // governed fact and verb below reaches it through these two objects.
  const sessionById = new Map(index.sessions.map((session) => [session.sessionId, session]));
  const rowOrgans: RailRowOrgans = {
    wide,
    // The Agents panel's rule (dispatchedSessions.ts): a parent named, the
    // launch ask kept, and not a native helper's record. Forks, roots and a
    // person's own sub-sessions are not hands.
    hand: (row) => {
      const shell = executionBySession.get(row.id);
      const ask = shell?.launchAsk?.trim();
      return shell !== undefined &&
        shell.helper == null &&
        typeof shell.parentThreadId === "string" &&
        shell.parentThreadId.length > 0 &&
        ask !== undefined &&
        ask.length > 0
        ? { ask }
        : null;
    },
    scopedGroup: roomId !== undefined,
    addressedId: activeSessionId,
    activePin,
    searching: needle.length > 0,
    filterKey: preferences.filter,
    foldChoices,
    onFoldRow: (foldKey, open) =>
      savePreferences({ ...preferences, sessionFolds: { ...foldChoices, [foldKey]: open } }),
    status: statusOf,
    descendantStatus: (id) => liveness.get(id)?.descendants ?? null,
    groupLabel: (groupId) => index.rooms.find((entry) => entry.roomId === groupId)?.label ?? null,
    address: (row) => address(row.id),
    openMenu: (row, position, pinShortcut) => {
      const session = sessionById.get(row.id);
      if (session !== undefined) openSessionMenu(session, position, pinShortcut);
    },
    threadRefOf: (row) => threadRefOf(row.id),
    hoverFacts: (row, meta) =>
      sessionHoverFacts({
        label: row.label,
        meta: [...meta],
        executions: executionListsBySession.get(row.id) ?? [],
      }),
    hoverDisabled: (row) => dragging || renaming?.sessionId === row.id,
    wrapRow: (row, children) => wrapSession(row, children),
    wrapPinnedRow: (row, children) => {
      const at = pinnedEntries.findIndex((entry) => entry.node.session.sessionId === row.id);
      const item: RoomRailDragData = {
        kind: "pin",
        id: row.id,
        parentId: worldKey,
        label: row.label,
      };
      return (
        <RoomRailReorderItem
          key={row.id}
          item={item}
          enabled={wide}
          canUp={at > 0}
          canDown={at < pinnedEntries.length - 1}
          onStep={(direction) => {
            const target = pinnedEntries[at + direction];
            if (target) move(item, target.node.session.sessionId, direction === 1);
          }}
        >
          {children}
        </RoomRailReorderItem>
      );
    },
    belowRow: (row) => {
      const session = sessionById.get(row.id);
      return session === undefined ? null : retirementControls(session);
    },
    pins: {
      pinned: (id) => preferences.pinnedSessionIds.includes(id),
      toggle: toggleSessionPin,
    },
    retire: (row) => {
      const session = sessionById.get(row.id);
      if (session !== undefined) retireSession(session);
    },
    // A World session leaves by retirement, never by the thread archive.
    archive: null,
    // A World's hover-only seats float over the name's tail as Local's do (20 September
    // 2026): reserved, the pin, ⋮ and retire cost about 110px and left a name 12px at
    // the dock's default width. The Pinned list's shortcuts stay reserved (RM13).
    hoverSeats: "floating",
    requestClose: (row) => requestClose(row.id, row.label),
    fileDrop: {
      targetId: fileDropTarget,
      handlers: (row) => fileDropHandlers({ sessionId: row.id, label: row.label }),
    },
    compactMetrics: compact ? (row) => compactMetrics(row.id) : null,
    rename:
      renaming === null
        ? null
        : {
            id: renaming.sessionId,
            title: renaming.title,
            onChange: (title) => setRenaming((prev) => (prev === null ? prev : { ...prev, title })),
            onCommit: commitRename,
            onCancel: () => setRenaming(null),
          },
    // A World births sub-sessions from the session's own strip, not the rail.
    childAsk: null,
  };
  const renderChip = (node: RoomRailSessionNode, breadcrumb?: string, pinShortcut = false) =>
    renderRailChip(rowOrgans, worldRailRow(node, null), breadcrumb, pinShortcut);

  if (roomId !== undefined && orderedGroups.rooms.length === 0) return null;
  // The principal's decision of 6 Oct 2026: retired sessions and archived
  // rooms stay out of the rail unless the person turns them on (one choice per
  // person for the whole World). The switch always says how many there are,
  // and a search still finds them, wearing their state.
  const showRetired = preferences.showRetiredSessions;
  const showArchived = preferences.showArchivedRooms;
  const toggleReveal = (field: "showRetiredSessions" | "showArchivedRooms") =>
    savePreferences({ ...preferences, [field]: !preferences[field] });
  // The rail's projection leaves archived rooms out; their retired sessions
  // are retired sessions all the same, counted and shown by this switch. The
  // general rail takes every archived room's, a Workroom's included: it is
  // the rail that lists archived rooms, and a Workroom's own rail is not
  // drawn once the room is archived.
  const parked = [
    ...scopedGroups.parked,
    ...archivedParkedRooms(index).filter((entry) =>
      roomId === undefined ? true : entry.room.roomId === roomId,
    ),
  ].toSorted((a, b) => a.room.bornSeq - b.room.bornSeq);
  const retiredCount = retiredSessionCount(parked);
  // With a switch off, the record he is on keeps its row: World entry, the
  // Workroom door, a link or the palette can land on a retired session or in
  // an archived room (the standing room included), and on main the Retired
  // group always showed it.
  const retiredItems =
    needle.length > 0
      ? searchParkedRooms(parked, needle)
      : showRetired
        ? parked
        : addressedParkedRooms(parked, scopedGroups.addressed);
  const retiredShown = needle.length > 0 || !showRetired ? retiredItems.length > 0 : true;
  const addressedRoomId = index.sessions.find(
    (session) => session.sessionId === activeSessionId,
  )?.roomId;
  const archivedItems =
    roomId !== undefined
      ? []
      : needle.length > 0
        ? searchArchivedRooms(archivedRooms, index, needle)
        : showArchived
          ? archivedRooms
          : archivedRooms.filter((entry) => entry.room.roomId === addressedRoomId);
  const archivedShown = needle.length > 0 || !showArchived ? archivedItems.length > 0 : true;
  // Open and Restore act only on the whole directory: while it is still
  // arriving the room's newest root may not be in yet (or none of its roots),
  // so both wait, disabled and saying "Loading…".
  const openArchivedRoom = (room: WorldRoomProjection) => {
    if (!roomsComplete) return;
    const sessionId = workroomEntrySessionId(index, room.roomId);
    if (sessionId === null) {
      toastManager.add({ type: "warning", title: "This archived room has no session to open" });
      return;
    }
    address(sessionId);
  };
  const openArchivedRoomMenu = (entry: ArchivedRoomEntry, position: { x: number; y: number }) => {
    void showContextMenuFallback(
      [
        {
          id: "archived-open",
          label: roomsComplete ? "Open room" : "Open room (Loading…)",
          disabled: !roomsComplete,
        },
        {
          id: "archived-restore",
          label: roomsComplete ? "Restore room" : "Restore room (Loading…)",
          // Restore names one of the room's root sessions (changeArchive).
          disabled:
            !roomsComplete ||
            archiveBusy.has(entry.room.roomId) ||
            rootSessionIdOf(index, entry.room.roomId) === undefined,
        },
        { id: "copy-room-id", label: "Copy room ID", icon: "copy" },
      ],
      position,
      { accent: RAIL_MENU_ACCENT },
    ).then((action) => {
      if (action === "archived-open") openArchivedRoom(entry.room);
      else if (action === "archived-restore") {
        if (roomsComplete) void changeArchive(entry.room, false);
      } else if (action === "copy-room-id")
        copyId(entry.room.roomId, { id: entry.room.roomId, noun: "Room" });
    });
  };
  const retiredGroup =
    wide && (retiredCount > 0 || showRetired) ? (
      <div className="flex flex-col gap-1 pt-4" data-room-rail-retired-foot="">
        <RailRevealToggle
          name="retired"
          label="Show retired sessions"
          count={retiredCount}
          on={showRetired}
          onToggle={() => toggleReveal("showRetiredSessions")}
        />
        {retiredShown ? (
          <RailGroup
            items={retiredItems}
            keyOf={(entry) => entry.room.roomId}
            label="Retired"
            latchKey={roomId === undefined ? RETIRED_LATCH_KEY : `${RETIRED_LATCH_KEY}:${roomId}`}
            name="retired"
            renderItem={(entry) => (
              <ParkedRoomCard
                archived={entry.room.archived === true}
                label={entry.room.label}
                onMenu={(position) => openParkedRoomMenu(entry, position)}
                onBrowseRoom={() => {
                  if (entry.sessions[0]) address(entry.sessions[0].sessionId);
                }}
                onBrowseSession={address}
                onRowMenu={(sessionId, position) => {
                  const session = entry.sessions.find(
                    (candidate) => candidate.sessionId === sessionId,
                  );
                  if (session !== undefined) openParkedSessionMenu(session, position);
                }}
                roomId={entry.room.roomId}
                sessions={entry.sessions.map((session) => ({
                  sessionId: session.sessionId,
                  label: session.label,
                }))}
              />
            )}
          />
        ) : null}
      </div>
    ) : null;
  const archivedGroup =
    wide && roomId === undefined && (archivedRooms.length > 0 || showArchived) ? (
      <div className="flex flex-col gap-1 pt-2" data-room-rail-archived-foot="">
        <RailRevealToggle
          name="archived"
          label="Show archived rooms"
          count={archivedRooms.length}
          on={showArchived}
          onToggle={() => toggleReveal("showArchivedRooms")}
        />
        {archivedShown ? (
          <RailGroup
            items={archivedItems}
            keyOf={(entry) => entry.room.roomId}
            label="Archived"
            latchKey={ARCHIVED_LATCH_KEY}
            name="archived"
            renderItem={(entry) => (
              <ArchivedRoomRow
                room={entry.room}
                sessionCount={entry.sessionCount}
                matches={entry.matches}
                loading={!roomsComplete}
                onOpen={() => openArchivedRoom(entry.room)}
                onOpenSession={address}
                onMenu={(position) => openArchivedRoomMenu(entry, position)}
              />
            )}
          />
        ) : null}
      </div>
    ) : null;
  const foldIds =
    roomId === undefined && cards.length > 0
      ? cards.map((card) => card.id)
      : orderedGroups.rooms.map((entry) => entry.room.roomId);
  // One room card (16 Oct 2026): the card header folds, starts a session and
  // arranges; its rows are the rail's own session chips, hands nested under
  // the session that launched them.
  // Can 80: the Earlier row at a room's foot, folding its quiet sessions.
  const earlierRow = (id: string, count: number, open: boolean) => (
    <button
      aria-expanded={open}
      className="ncl-room-earlier mt-1 flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-[11px] text-muted-foreground hover:bg-accent/40 hover:text-foreground"
      data-room-rail-earlier={id}
      onClick={() => toggleEarlier(id)}
      title={
        open
          ? "Fold the quiet sessions away again"
          : `${count} session${count === 1 ? "" : "s"} quiet for ${preferences.quietDays === 1 ? "a day" : `${preferences.quietDays} days`} or more`
      }
      type="button"
    >
      {open ? "Hide earlier" : `Earlier · ${count}`}
    </button>
  );
  const renderCard = (card: RoomRailCard, index: number) => {
    const quietCount = quietCountByCard.get(card.id) ?? 0;
    const earlier = earlierOpen.has(card.id);
    const open = needle.length > 0 || (roomFolds[roomFoldKey(card.id)] ?? true);
    const listId = `room-card-sessions-${card.id}`;
    const item: RoomRailDragData = {
      kind: "room",
      id: card.id,
      parentId: worldKey,
      label: card.label,
    };
    return (
      <RoomRailReorderItem
        key={card.id}
        item={item}
        enabled={wide && needle.length === 0}
        canUp={index > 0}
        canDown={index < cards.length - 1}
        onStep={(direction) => {
          const target = cards[index + direction];
          if (target) moveCard(card.id, target.id, direction === 1);
        }}
      >
        <div
          aria-current={card.addressed ? "page" : undefined}
          className={cn("ncl-room-group", card.addressed && "is-focused")}
          data-room-card={card.id}
          data-room-card-kind={card.kind}
          data-room-rail-room-block={card.id}
          data-workroom-key={card.workroomKey ?? undefined}
          data-open={open ? "true" : "false"}
          style={roomCardStyle(card)}
        >
          <RoomCardHeader
            card={card}
            open={open}
            listId={listId}
            actions={roomCardActions(cards, index, false, tidyCardIds.has(card.id))}
            onToggle={() => saveRoomFolds({ ...roomFolds, [roomFoldKey(card.id)]: !open })}
            onNew={() => newWorkroomSession({ workroomKey: card.workroomKey, label: card.label })}
            newButton={
              card.kind === "general" ? (
                <NewGeneralRoomButton
                  key={worldKey}
                  appearance="card"
                  worldKey={state.worldKey}
                  acceptEntryReadback={view.acceptEntryReadback}
                />
              ) : undefined
            }
            count={
              flattenRailNodes(card.sessions).filter(
                (node) => statusOf(node.session.sessionId) !== null,
              ).length
            }
            onAction={(action) => cardAction(card, index, action)}
          />
          {open ? (
            <div className="flex min-w-0 flex-col pb-1 pl-1" data-room-rail-sessions="" id={listId}>
              {card.sessions.length === 0 && quietCount === 0 ? (
                <p className="ncl-room-empty">No sessions yet</p>
              ) : card.kind === "general" ? (
                // A general room is what the cockpit rail calls a session: it
                // keeps its own header (rename, archive, status, hover card).
                worldRailNavigation({
                  ...groups,
                  rooms: (earlier ? groups : recentGroups).rooms.filter(
                    (entry) => entry.room.kind === "general",
                  ),
                }).groups.map((group) => renderRailGroup(groupOrgans, rowOrgans, group))
              ) : (
                card.sessions.map((node) => renderRailChip(rowOrgans, worldRailRow(node, null)))
              )}
              {quietCount > 0 ? earlierRow(card.id, quietCount, earlier) : null}
            </div>
          ) : null}
        </div>
      </RoomRailReorderItem>
    );
  };
  const isScopedRoom = roomId !== undefined;
  const roomEntryById = new Map(groups.rooms.map((entry) => [entry.room.roomId, entry]));
  const groupOrgans: RailGroupOrgans = {
    wide,
    open: (group) => roomFolds[roomFoldKey(group.id)] ?? true,
    renderHeader: renderRoomHeader,
    header: (group, open) => {
      const roomId = group.id;
      const foldKey = roomFoldKey(roomId);
      // Status follows Room membership, including headless work and children
      // of retired/hidden parents that the visible tree does not render.
      const sessionStatuses = index.sessions
        .filter((session) => session.roomId === roomId && session.lifecycle !== "retired")
        .map((session) => statusOf(session.sessionId));
      const rollup = sessionStatuses.reduce<RootSessionStatus | null>(
        moreUrgentRootSessionStatus,
        null,
      );
      const current = index.sessions.some(
        (session) => session.sessionId === activeSessionId && session.roomId === roomId,
      );
      const entry = roomEntryById.get(roomId)!;
      return {
        current,
        face: sessionFace(rollup),
        label: group.label,
        renaming:
          renamingRoom?.roomId === roomId
            ? {
                title: renamingRoom.title,
                onChange: (title) =>
                  setRenamingRoom((prev) => (prev === null ? prev : { ...prev, title })),
                onCommit: commitRoomRename,
                onCancel: () => setRenamingRoom(null),
              }
            : null,
        onClose: () => archiveRoom(entry),
        archiveBusy: archiveBusy.has(roomId),
        onMenu: (position) => openRoomMenu(entry, position),
        onToggle: () => saveRoomFolds({ ...roomFolds, [foldKey]: !open }),
        open,
        roomId,
        sessionStatuses,
        standing: group.standing,
        wide,
        preview: roomHoverFacts({
          label: group.label,
          meta: [
            current ? "Addressed" : null,
            stripRoomLabel(roomStatusDescription(group.label, sessionStatuses), group.label),
          ],
          executions: index.sessions
            .filter((session) => session.roomId === roomId)
            .flatMap((session) => executionListsBySession.get(session.sessionId) ?? []),
        }),
        previewDisabled: dragging || renamingRoom?.roomId === roomId,
      };
    },
    wrapGroup: (group, children) => {
      const roomId = group.id;
      const at = groups.rooms.findIndex((candidate) => candidate.room.roomId === roomId);
      const item: RoomRailDragData = {
        kind: "room",
        id: roomId,
        parentId: worldKey,
        label: group.label,
      };
      return (
        <RoomRailReorderItem
          key={roomId}
          item={item}
          enabled={wide && !isScopedRoom}
          canUp={at > 0}
          canDown={at < groups.rooms.length - 1}
          onStep={(direction) => {
            const target = groups.rooms[at + direction];
            if (target) move(item, target.room.roomId, direction === 1);
          }}
          sortControls={
            <RoomRailSortSelect
              label="Sessions in this Room"
              value={preferences.sortOverrides[roomId] ?? "inherit"}
              inherit
              onChange={(sort) => setSessionSort(roomId, sort)}
            />
          }
        >
          {children}
        </RoomRailReorderItem>
      );
    },
    foot: () => (isScopedRoom ? retiredGroup : null),
    emptyBody: null,
  };
  const rows = (
    <>
      <div
        // FD-CAN-29: the chips are TABS on FD-CAN-27's rhythm in both densities.
        className={cn(
          "ncl-reactbits flex w-full flex-none flex-col",
          cards.length > 0 ? "ncl-room-rail gap-2" : "gap-4",
        )}
        data-room-rail-list=""
      >
        {listedSessions !== null
          ? listedSessions.map((entry) => renderChip(entry.node, entry.breadcrumb))
          : roomId === undefined
            ? cards.map(renderCard)
            : worldRailNavigation(groups).groups.map((group) =>
                renderRailGroup(groupOrgans, rowOrgans, group),
              )}
        {flatView && wide && flatQuietCount > 0
          ? earlierRow(EARLIER_FLAT_KEY, flatQuietCount, earlierOpen.has(EARLIER_FLAT_KEY))
          : null}
        {roomsComplete &&
        (listedSessions !== null
          ? listedSessions.length === 0
          : roomId === undefined
            ? cards.length === 0
            : groups.rooms.length === 0) &&
        // A search that finds only retired or archived records lists them below.
        !(needle.length > 0 && (retiredItems.length > 0 || archivedItems.length > 0)) ? (
          <p className={wide ? "px-2 py-4 text-xs text-muted-foreground" : "sr-only"}>
            No matching sessions
          </p>
        ) : null}
        {roomsComplete ? null : (
          <p
            className={wide ? "px-2 py-2 text-xs text-muted-foreground" : "sr-only"}
            data-world-rooms-loading=""
            role="status"
          >
            {WORLD_ROOMS_LOADING_COPY}
          </p>
        )}
      </div>
      {/* Workroom W0 review: a launch seat never silences a refusal. With
          the door chips retired (16 Oct 2026) the World's own sentence
          stands below the rooms, in both densities. */}
      {roomId === undefined && launchOutcome.refusal !== null ? (
        <p
          className={cn(
            "text-xs text-muted-foreground",
            wide ? "px-2 pt-3" : "flex justify-center pt-3",
          )}
          data-dock-workrooms-refusal=""
          role="alert"
          title={launchOutcome.refusal.reason}
        >
          {wide ? (
            launchOutcome.refusal.reason
          ) : (
            <>
              <CircleAlert aria-hidden className="size-4" />
              <span className="sr-only">{launchOutcome.refusal.reason}</span>
            </>
          )}
        </p>
      ) : null}
      {/* RM-CAN-10 law 5: the room's memory — the Retired group, wide only
        (nesting and memory are wide affordances; the tight dock keeps its
        circle-only grammar). */}
      {roomId === undefined ? retiredGroup : null}
      {archivedGroup}
    </>
  );
  return (
    <RoomRailDragArea
      className={scrollFrame ? "flex min-h-0 flex-1 flex-col" : undefined}
      contentClassName={scrollFrame ? "flex min-h-0 flex-1 flex-col" : undefined}
      onMove={move}
      onCross={drop}
      conversationOf={(item) => {
        const thread = threadRefOf(item.id);
        return thread === null ? null : { threadId: String(thread.threadId), title: item.label };
      }}
      onDragging={(value) => {
        setDragging(value);
        setFrozenActivity((previous) => (value ? (previous ?? activity) : null));
      }}
    >
      <div
        className={cn(
          "flex w-full flex-col",
          scrollFrame ? "min-h-0 flex-1" : "flex-none",
          wide ? "" : "items-center",
        )}
        data-room-rail=""
        data-room-rail-authority="world"
        ref={railRef}
        onPointerEnter={() => setFrozenActivity(activity)}
        onFocusCapture={() => {
          if (frozenActivity === null) setFrozenActivity(activity);
        }}
        onPointerLeave={(event) => {
          if (!dragging && !event.currentTarget.contains(document.activeElement))
            setFrozenActivity(null);
        }}
        onBlurCapture={(event) => {
          if (!dragging && !event.currentTarget.contains(event.relatedTarget as Node | null))
            setFrozenActivity(null);
        }}
      >
        {/* The principal, 18 Oct 2026 ("should not scroll above this"): the
            header stands still; only the rows below it scroll. */}
        <div
          className={cn("flex w-full flex-none flex-col", wide ? "" : "items-center")}
          data-room-rail-header=""
        >
          {pinnedEntries.length > 0 ? (
            <section
              aria-label="Pinned sessions"
              data-room-rail-pinned=""
              className="mb-5 flex w-full flex-none flex-col gap-1.5"
            >
              <div className="relative mb-3">
                {/* Keep the first pin below the panel curve in both dock widths. */}
                <span
                  aria-hidden={!wide}
                  className={cn(
                    "block overflow-hidden whitespace-nowrap px-1 pb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground",
                    wide ? "pr-8" : "invisible",
                  )}
                >
                  Pinned
                </span>
                {roomId === undefined && cards.length === 0 ? (
                  <div
                    className={cn(
                      "absolute inset-y-0 flex items-center justify-center",
                      wide ? "right-1" : "inset-x-0",
                    )}
                  >
                    <NewGeneralRoomButton
                      key={worldKey}
                      worldKey={state.worldKey}
                      acceptEntryReadback={view.acceptEntryReadback}
                      workrooms={plusWorkrooms}
                      onNewWorkroomSession={onPlusWorkroom}
                    />
                  </div>
                ) : null}
              </div>
              {!wide ? (
                <Pin aria-hidden="true" className="mx-auto size-3 text-muted-foreground" />
              ) : null}
              {pinnedEntries.map((entry) => renderChip(entry.node, entry.breadcrumb, true))}
            </section>
          ) : null}
          {/* FD-CAN-29: keep the Rooms controls clear of the first row. In the
            wide dock the search row below does that, so the Rooms row sits
            close above it (the principal, 18 Oct 2026: "reduce vertical
            space"); the tight dock has no search row and keeps its gap. */}
          {roomId === undefined ? (
            <div
              className={cn(
                "flex items-center gap-1",
                wide ? "pb-1" : pinnedEntries.length > 0 ? "pb-4" : "pb-7",
                wide ? "px-1" : "flex-col",
              )}
            >
              {wide ? (
                <span className="flex-1 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                  {flatView ? "Sessions" : "Rooms"}
                </span>
              ) : null}
              <RoomArchiveMenu
                rooms={archivedRooms}
                onRestore={(room) => changeArchive(room, false)}
                renderArrangement={(closeMenu) => (
                  <RoomRailArrangementControls
                    preferences={preferences}
                    showRoomFolds={wide}
                    compact={compact}
                    onCompactChange={setCompact}
                    onTidy={() => {
                      closeMenu();
                      openTidy({ scope: null, label: null });
                    }}
                    onChange={(next) => commitArrangement(next)}
                    allFolded={
                      foldIds.length > 0 &&
                      foldIds.every((id) => roomFolds[roomFoldKey(id)] === false)
                    }
                    onFoldAll={() => {
                      const open = foldIds.every((id) => roomFolds[roomFoldKey(id)] === false);
                      saveRoomFolds({
                        ...roomFolds,
                        ...Object.fromEntries(foldIds.map((id) => [roomFoldKey(id), open])),
                      });
                    }}
                  />
                )}
              />
              {pinnedEntries.length === 0 && cards.length === 0 ? (
                <NewGeneralRoomButton
                  key={worldKey}
                  worldKey={state.worldKey}
                  acceptEntryReadback={view.acceptEntryReadback}
                  workrooms={plusWorkrooms}
                  onNewWorkroomSession={onPlusWorkroom}
                />
              ) : null}
            </div>
          ) : null}
          {wide && roomId === undefined ? (
            // UP13: one search row; the scope (the same filter the Arrange menu
            // keeps) folds into it instead of a second row.
            <div className="mb-3 flex items-center gap-1 px-1" data-room-rail-search-row="">
              <input
                aria-label="Search sessions"
                className="h-7 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-xs text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                data-room-rail-search=""
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  event.stopPropagation();
                  if (event.key === "Escape") setQuery("");
                }}
                placeholder="Search sessions"
                type="search"
                value={query}
              />
              <select
                aria-label="Scope"
                className={cn(
                  "h-7 max-w-24 rounded-md border border-input bg-background px-1 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                  preferences.filter === "all" ? "text-muted-foreground" : "text-foreground",
                )}
                data-room-rail-scope={preferences.filter}
                onChange={(event) =>
                  savePreferences({ ...preferences, filter: event.target.value as RoomRailScope })
                }
                title="Scope"
                value={preferences.filter}
              >
                {Object.entries(ROOM_RAIL_SCOPES).map(([scope, label]) => (
                  <option key={scope} value={scope}>
                    {scope === "all" ? "All" : label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {preferences.filter !== "all" && !(wide && roomId === undefined) ? (
            <button
              type="button"
              aria-label={`${preferences.filter === "needs" ? "Needs you" : "Working"} · Clear filter`}
              title={`${preferences.filter === "needs" ? "Needs you" : "Working"} · Clear filter`}
              className={cn(
                "mb-3 rounded text-xs text-muted-foreground hover:bg-accent",
                wide
                  ? "self-start border border-border px-2 py-1"
                  : "flex size-7 items-center justify-center",
              )}
              onClick={() => savePreferences({ ...preferences, filter: "all" })}
            >
              {wide ? (
                `${preferences.filter === "needs" ? "Needs you" : "Working"} · Clear filter`
              ) : (
                <FilterX aria-hidden="true" className="size-3.5" />
              )}
            </button>
          ) : null}
        </div>
        {scrollFrame ? scrollFrame(rows) : rows}
        {closeWarning}
        {tidy !== null ? (
          <SessionTidyDialog
            worldKey={worldKey}
            scope={tidy.scope}
            roomLabel={tidy.label}
            keep={tidyKeep}
            initialDays={preferences.quietDays > 0 ? preferences.quietDays : 3}
            run={tidyRun.run}
            onArchive={tidyRun.start}
            onClose={() => {
              setTidy(null);
              tidyRun.dismiss();
            }}
          />
        ) : null}
      </div>
    </RoomRailDragArea>
  );
}
